import '../../platform.css';
import './home.css';

import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { teamsState } from '../../recoil/atoms/teamsState';
import { competitionsState } from '../../recoil/atoms/competitionsState';
import { playerOverviewsState } from '../../recoil/atoms/playerOverviewsState';
import { tenantSettingsState } from '../../recoil/atoms/tenantSettingsState';
import { useSetNavigationProps } from '../../recoil/hooks/useSetNavigationProps';
import { useSetSelectedPlayer } from '../../recoil/hooks/useSetSelectedPlayer';
import { useAuthContext } from '../../../common/contexts/useAuthContext';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import RocketIcon from '@mui/icons-material/Rocket';
import EqualizerIcon from '@mui/icons-material/Equalizer';

import { translate } from '../../../common/language/translations';
import { PlatformHistory } from '../../components/history/PlatformHistory';
import { HomeViewLinePlot } from '../../components/plots/HomeViewLinePlot';
import { Toggle } from '../../components/controls/input/Toggle';
import { getHomePageData } from '../../services/firestore/homePage';
import { getPlayerOverviews } from '../../services/server/application/playerOverviews';
import { FixedTabs } from '../../components/controls/navigation/FixedTabs';
import { TeamTable } from '../../components/tables/teamTable/TeamTable';
import { getPlatformHistoryLast14Days } from '../../services/firestore/activities';
import { StringToAnyMap, StringToStringMap } from '../../types/common';
import { PlayerId, PlayerOverviews } from '../../types/player';
import { Activity } from '../../types/activity';
import { PlayerReport } from '../../types/report';
import { getTenantColor, getUseAlternativeTierColors } from '../../static/tenantConfigs';
import { alternativeTierColors, tierColors } from '../../static/constants/colors';


export const Home: React.FC = () => {

  const { currentUser } = useAuthContext();

  const updateNavigationProps = useSetNavigationProps();

  const userConfig = useRecoilValue(userConfigState);
  const teams = useRecoilValue(teamsState);
  const tenantSettings = useRecoilValue(tenantSettingsState);
  const competitions = useRecoilValue(competitionsState);
  const playerOverviews = useRecoilValue(playerOverviewsState);
  const { setAndTrackSelectedPlayer } = useSetSelectedPlayer();

  const [activeLineTab, setActiveLineTab] = useState(0);
  const [activeTableTab, setActiveTableTab] = useState(0);

  const [tableDataArray, setTableDataArray] = useState<StringToAnyMap[][]>([]);
  const [tableOptionsArray, setTableOptionsArray] = useState<string[]>([]);
  const [lineData, setLineData] = useState<StringToAnyMap | undefined>(undefined);
  const [comparisonCompetitionIdToTierMap, setComparisonCompetitionIdToTierMap] = useState<StringToAnyMap>({});
  const [tenantName, setTenantName] = useState<string | undefined>(undefined);
  const [tenantCountryCode, setTenantCountryCode] = useState<string | undefined>(undefined);

  const [platformHistory, setPlatformHistory] = useState<(Activity | PlayerReport)[]>([]);
  const [additionalPlayerOverviews, setAdditionalPlayerOverviews] = useState<PlayerOverviews>({});

  const [isAdvancedTeamDataToggled, setIsAdvancedTeamDataToggled] = useState(false);


  const lineTabOptions = [
    <EqualizerIcon key={'equializer-icon'} style={{ fontSize: 21, marginTop: 1 }} />,
    <CalendarMonthIcon key={'calendar-icon'} style={{ fontSize: 21, marginTop: 1 }} />,
    <RocketIcon key={'rocket-icon'} style={{ fontSize: 21, marginTop: 1 }} />,
  ];
  const lineTabTitles = [
    'xGDataTitle',
    'averageAgePerMinutePlayedTitle',
    'u21MinutesPerMinuteTitle'
  ];


  const handlePlayerClick = (playerId: PlayerId, playerName: string, report?: PlayerReport) => {
    const numberId = Number(playerId);
    const playerOverview = !isNaN(numberId) ? (playerOverviews[numberId] ?? additionalPlayerOverviews[numberId]) : undefined;

    // report should never be passed if user does not have report access
    const reportToUse = userConfig?.accessConfig.reportAccess === 'none' ? undefined : report;

    setAndTrackSelectedPlayer({
      id: playerId,
      name: playerName,
      playerOverview: playerOverview,
      selectedReport: reportToUse,
      activeSubTab: reportToUse ? 4 : undefined,
    });
  };


  const handleTeamClick = (teamId: string) => {
    if (teamId === 'ownTeam') {
      updateNavigationProps({ activeNavigationTab: 'ownTeam', activeOwnTeamSubTab: 'playerSquad' }, true);
    }

    else if (teamId === 'masterTeam' || teamId === 'shadowTeam') {
      updateNavigationProps({ activeNavigationTab: teamId }, true);
    }

    else if (teams?.[teamId]?.isAcademyTeam) {
      updateNavigationProps({ activeNavigationTab: 'academy', activeAcademyTeamId: teamId }, true);
    }

    else {
      updateNavigationProps({ activeNavigationTab: 'scoutTeams', activeScoutTeamId: teamId }, true);
    }
  };


  useEffect(() => {
    if (userConfig && tenantSettings && competitions) {

      const sortedIterationIdsWithTableData: string[] = [];
      const iterationIdToTableOption: StringToStringMap = {};
      const competitionIdsToCompareWith: Set<string> = new Set();

      if (tenantSettings.iterations) {
        tenantSettings.iterations.forEach((iteration: StringToAnyMap) => {
          const competition = competitions[iteration.competitionId];
          if (competition && competition.type === 'League') {
            sortedIterationIdsWithTableData.push(iteration.iterationId.toString());
            iterationIdToTableOption[iteration.iterationId.toString()] = competition.name + ' ' + iteration.season;
            competitionIdsToCompareWith.add(iteration.competitionId);
          }
        });
      }

      const competitionIdToTierMap: StringToAnyMap = {};
      competitionIdsToCompareWith.forEach((competitionId) => {
        competitionIdToTierMap[competitionId] = competitions[parseInt(competitionId)].tier;
      });

      getHomePageData(
        tenantSettings.clubId,
        sortedIterationIdsWithTableData,
        iterationIdToTableOption,
        competitionIdsToCompareWith,
        currentUser
      ).then((homeViewData) => {
        setTableOptionsArray(homeViewData.tableOptions);
        setTableDataArray(homeViewData.tableData);
        setLineData(homeViewData.lineData);
        setComparisonCompetitionIdToTierMap(competitionIdToTierMap);
        setTenantName(tenantSettings.clubName);
        setTenantCountryCode(tenantSettings.countryCode ?? 'NO');
      });
    }

  }, [currentUser, userConfig, tenantSettings, competitions]);


  useEffect(() => {
    if (currentUser && userConfig && teams) {
      getPlatformHistoryLast14Days(userConfig, currentUser).then((result) => {

        // set platform history
        const history = result.platformHistory;
        setPlatformHistory(history);

        // get player overviews for player that may be accessed from the platform history
        const playerIds = result.playerIds;
        const playerIdsToFetch = Array.from(playerIds).filter(id => !playerOverviews[id]);
        if (playerIdsToFetch.length > 0) {
          getPlayerOverviews(playerIdsToFetch, currentUser).then((additionalPlayerOverviews) => {
            setAdditionalPlayerOverviews(additionalPlayerOverviews ?? {});
          });
        }
      });
    }
  }, [userConfig?.club, currentUser, teams]); // eslint-disable-line react-hooks/exhaustive-deps


  const tenantColor = getTenantColor(userConfig?.club ?? '');
  const useAlternativeTierColors = getUseAlternativeTierColors(userConfig?.club ?? '');


  return (
    <div className='home-view-container'>

      <div className='home-view-inner-container'>
        <div className='home-view-column home-view-left-column'>
          <div className='home-view-history-section-container'>
            <FixedTabs
              tabOptions={lineTabOptions}
              tabTitles={['xGData', 'averageAgePerMinutePlayed', 'u21MinutesPerMinute']}
              activeTab={activeLineTab}
              setActiveTab={setActiveLineTab}
              parentTab={'home'}
              withoutTabLine={true}
            />

            <div className='home-view-section-title home-view-history-section-title'>
              {lineData ? translate(lineTabTitles[activeLineTab], userConfig?.language) + ' ' + tenantName : ''}
            </div>

            {activeLineTab === 0 && lineData && (
              <div className='home-view-history-section-labels'>
                <div className='home-view-history-section-label'>
                  <div className='home-view-history-section-label-line' style={{ backgroundColor: '#6abe88' }} />
                  <div className='home-view-history-section-label-text'>
                    {translate('xG', userConfig?.language)}
                  </div>
                </div>

                <div className='home-view-history-section-label'>
                  <div className='home-view-history-section-label-line' style={{ backgroundColor: '#c77070' }} />
                  <div className='home-view-history-section-label-text'>
                    {translate('xGC', userConfig?.language)}
                  </div>
                </div>
              </div>
            )}

            {activeLineTab > 0 && lineData && (
              <div className='home-view-history-section-labels home-view-history-section-labels-wide'>
                <div className='home-view-history-section-label'>
                  <div className='home-view-history-section-label-line' style={{ backgroundColor: tenantColor }} />
                  <div className='home-view-history-section-label-text'>
                    {tenantName}
                  </div>
                </div>

                {Object.entries(comparisonCompetitionIdToTierMap).map(([competitionId, tier]) => {
                  return (
                    <div className='home-view-history-section-label' key={competitionId}>
                      <div className='home-view-history-section-label-line' style={{
                        backgroundColor: useAlternativeTierColors ? alternativeTierColors[tier] : tierColors[tier]
                      }} />
                      <div className='home-view-history-section-label-text'>
                        {competitions[Number(competitionId)].name}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className='home-view-history-section-line-plot-container'>
              {lineData && (
                <HomeViewLinePlot
                  data={lineData}
                  activeTab={activeLineTab}
                  tenantColor={tenantColor}
                  useAlternativeTierColors={useAlternativeTierColors}
                  competitionIdToTierMap={comparisonCompetitionIdToTierMap}
                />
              )}
            </div>
          </div>

          <div className='home-view-activity-section-container'>
            <div className='home-view-section-title home-view-activity-section-title'>
              {translate('activityLast14Days', userConfig?.language)}
            </div>

            <div className='home-view-activity-section-log-container'>
              <PlatformHistory
                history={platformHistory}
                handlePlayerClick={handlePlayerClick}
                handleTeamClick={handleTeamClick}
              />
            </div>
          </div>
        </div>

        <div className='home-view-column home-view-right-column'>
          <FixedTabs
            tabOptions={tableOptionsArray}
            activeTab={activeTableTab}
            setActiveTab={setActiveTableTab}
            parentTab={'home'}
            withoutTabLine={true}
          />
          {tableDataArray.length > 0 && (
            <div className='home-view-table-toggle-section'>
              <div className='home-view-table-toggle-section-text'>
                {translate('advancedData', userConfig?.language)}
              </div>
              <div className='home-view-table-toggle-section-toggle'>
                <Toggle isToggled={isAdvancedTeamDataToggled} setIsToggled={setIsAdvancedTeamDataToggled} boxShadow={'0px 0px 3px 1px #181a23aa'} />
              </div>
            </div>
          )}
          {userConfig && tableDataArray.length > 0 && tenantCountryCode && (
            <div className='home-view-table-container'>
              <TeamTable
                data={tableDataArray[activeTableTab]}
                isAdvancedTeamDataToggled={isAdvancedTeamDataToggled}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
